// Session/auth state machine. The server session (GET /auth/session) is the only
// source of identity; the client never infers roles from an email address.
import type { Session } from '@bcio-voice/contracts';
import type { ApiRequestError } from '../lib/api.ts';

export type AuthState =
  | { kind: 'booting' }
  | { kind: 'boot_error'; error: ApiRequestError | null }
  | { kind: 'signed_out'; session: Session; notice: SignOutNotice }
  | { kind: 'choose_tenant'; session: Session }
  | { kind: 'ready'; session: Session }
  /** Session ended while a call was live: keep the dock, ask to sign in after the call. */
  | { kind: 'expired_in_call'; session: Session; anonymous: Session | null; notice: 'expired' | 'policy' };

/**
 * Why the sign-in screen is shown: `policy` means the role now needs a fresh sign-in (for example
 * MFA). The identity workflows (B6) end the session on purpose: a password reset or two-step
 * enrollment signs the account out everywhere, and joining a workspace needs a new sign-in.
 * The `_verified` notices (and `mfa_enrolled`) follow a step that just used an authenticator
 * code: the server won't accept that code again, so the next sign-in needs the app's next code.
 */
export type SignOutNotice =
  | 'expired'
  | 'signed_out'
  | 'policy'
  | 'password_reset'
  | 'password_reset_verified'
  | 'mfa_enrolled'
  | 'invite_joined'
  | 'invite_joined_verified'
  | 'fresh_sign_in'
  | null;

/** True after a workflow that used up the current authenticator code. */
export function usedAuthenticatorCode(notice: SignOutNotice): boolean {
  return notice === 'password_reset_verified' || notice === 'mfa_enrolled' || notice === 'invite_joined_verified';
}

export type AuthEvent =
  | { type: 'session_loaded'; session: Session }
  | { type: 'boot_failed'; error: ApiRequestError | null }
  | { type: 'expired'; anonymous: Session | null; liveCall: boolean; notice?: 'expired' | 'policy' }
  | { type: 'signed_out'; anonymous: Session; notice?: SignOutNotice }
  | { type: 'call_finished' };

export function fromSession(session: Session, notice: SignOutNotice = null): AuthState {
  if (!session.user) return { kind: 'signed_out', session, notice };
  if (!session.tenant) return { kind: 'choose_tenant', session };
  return { kind: 'ready', session };
}

export function authReducer(state: AuthState, event: AuthEvent): AuthState {
  switch (event.type) {
    case 'session_loaded': {
      // A different user signing in while a call is shown must not inherit the previous view.
      return fromSession(event.session);
    }
    case 'boot_failed':
      return { kind: 'boot_error', error: event.error };
    case 'expired': {
      const notice = event.notice ?? 'expired';
      if (state.kind === 'ready' && event.liveCall) return { kind: 'expired_in_call', session: state.session, anonymous: event.anonymous, notice };
      if (state.kind === 'expired_in_call') return { ...state, anonymous: event.anonymous ?? state.anonymous, notice: notice === 'policy' ? 'policy' : state.notice };
      const anonymous = event.anonymous ?? anonymousFrom(state);
      return anonymous ? { kind: 'signed_out', session: anonymous, notice } : { kind: 'boot_error', error: null };
    }
    case 'signed_out':
      return { kind: 'signed_out', session: event.anonymous, notice: event.notice === undefined ? 'signed_out' : event.notice };
    case 'call_finished':
      if (state.kind !== 'expired_in_call') return state;
      return state.anonymous ? { kind: 'signed_out', session: state.anonymous, notice: state.notice } : { kind: 'boot_error', error: null };
    default:
      return state;
  }
}

function anonymousFrom(state: AuthState): Session | null {
  if (state.kind === 'signed_out') return state.session;
  return null;
}

export function currentSession(state: AuthState): Session | null {
  return 'session' in state ? state.session : null;
}

/** The CSRF token to send; the anonymous session's token is used to sign in. */
export function csrfFor(state: AuthState): string | null {
  if (state.kind === 'expired_in_call') return state.anonymous?.csrf_token ?? null;
  return currentSession(state)?.csrf_token ?? null;
}

export type LoginStep = 'credentials' | 'mfa' | 'enrollment_required' | 'limited';

/** Next sign-in step for a login error code (server: apps/api/src/server.ts). */
export function loginStepFor(code: string, current: LoginStep): LoginStep {
  switch (code) {
    case 'mfa_required':
      return 'mfa';
    case 'mfa_enrollment_required':
      return 'enrollment_required';
    case 'login_limited':
    case 'rate_limited':
      return 'limited';
    default:
      return current;
  }
}

/** Milliseconds until expires_at, or null when the session has no expiry. */
export function msUntilExpiry(session: Session | null, now: number): number | null {
  if (!session?.expires_at) return null;
  const at = Date.parse(session.expires_at);
  return Number.isNaN(at) ? null : Math.max(0, at - now);
}

export function canSwitchTenant(session: Session | null, liveCall: boolean): { allowed: boolean; reason: string | null } {
  if (!session || session.memberships.length < 2) return { allowed: false, reason: null };
  if (liveCall) return { allowed: false, reason: 'Finish your call before switching workspaces.' };
  return { allowed: true, reason: null };
}
